import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { message, Modal, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, GripVertical, Play, Video } from "lucide-react";
import {
  DELETE_TRAINING_VIDEO,
  GET_COURSE_DETAILS,
  isSchemaMissingError,
  REORDER_TRAINING_VIDEOS,
  trainingApiAvailability,
} from "../../../../services/userAPI";
import TrainingModuleEditIcon from "../../../../Images/trainingModuleEditIcon.png";
import TrainingModuleDeleteIcon from "../../../../Images/trainingModuleDeleteIcon.png";
import TrainingModuleTimingIcon from "../../../../Images/trainingModuleTimingIcon.png";
import TrainingModuleLanguageIcon from "../../../../Images/trainingModuleLanguageIcon.png";

const formatPublishDate = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

const pad2 = (n) => String(n ?? 0).padStart(2, "0");

const CourseModuleDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const courseId = location.state?.course?.id;

  const { data: courseData, loading: courseLoading } = useQuery(GET_COURSE_DETAILS, {
    variables: { courseId },
    skip: !courseId || !trainingApiAvailability.getCourseDetails,
    fetchPolicy: "network-only",
    onError: (err) => {
      if (isSchemaMissingError(err)) trainingApiAvailability.getCourseDetails = false;
    },
  });
  const apiCourse = courseData?.getCourseDetails;
  const [deleteTrainingVideo] = useMutation(DELETE_TRAINING_VIDEO);
  const [reorderTrainingVideos] = useMutation(REORDER_TRAINING_VIDEOS);

  const [videos, setVideos] = useState([]);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [removedIds, setRemovedIds] = useState([]);
  const dragIndex = useRef(null);

  useEffect(() => {
    if (!apiCourse?.videos) return;
    const mapped = apiCourse.videos
      .slice()
      .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
      .map((v, i) => ({
        id: v.id,
        title: v.video?.title || `Video ${v.rank ?? i + 1}`,
        description: v.video?.description || "",
        duration: `${pad2(v.video?.videoLengthMins)}:${pad2(v.video?.videoLengthSecs)} Min`,
        language: v.video?.language || "",
        languageId: v.video?.languageId ?? null,
        thumbnail: v.video?.vimeoThumbnailUrl || "",
        videoCode:
          v.video?.vimeoThumbnailUrl?.match(/\/vi\/([^/]+)\//)?.[1] || "",
        contentId: v.contentId,
      }));
    setVideos(mapped);
  }, [apiCourse]);

  const visibleVideos = videos.filter((v) => !removedIds.includes(v.id));

  const course = apiCourse
    ? {
        title: apiCourse.courseName,
        description: apiCourse.courseDescription,
        thumbnailPreview: apiCourse.thumbnailImage_ || apiCourse.thumbnailUrl,
        publishDate: apiCourse.publishDate,
        videoCount: apiCourse.videoCount ?? apiCourse.videos?.length ?? 0,
        duration: `${pad2(apiCourse.totalLengthMins)}:${pad2(apiCourse.totalLengthSecs)} Min`,
        productStatus:
          apiCourse.publishDate &&
          apiCourse.publishDate > new Date().toISOString().split("T")[0]
            ? "DRAFT"
            : "ACTIVE",
      }
    : null;

  const handleDrop = (targetIndex) => {
    const fromIndex = dragIndex.current;
    if (fromIndex === null || fromIndex === targetIndex) return;
    setVideos((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIndex, 1);
      arr.splice(targetIndex, 0, moved);
      if (courseId && trainingApiAvailability.reorderTrainingVideos) {
        reorderTrainingVideos({
          variables: { courseId, trainingVideoIds: arr.map((v) => v.id) },
        }).catch((err) => {
          if (isSchemaMissingError(err)) {
            trainingApiAvailability.reorderTrainingVideos = false;
          } else {
            message.error("Reorder failed: " + err.message);
          }
        });
      }
      return arr;
    });
  };

  const handleDelete = () => {
    const videoId = deleteCandidate?.id;
    const removeLocally = () => {
      setRemovedIds((prev) => [...prev, videoId]);
      setVideos((prev) => prev.filter((v) => v.id !== videoId));
    };
    if (courseId && trainingApiAvailability.deleteTrainingVideo) {
      deleteTrainingVideo({ variables: { trainingVideoId: videoId } })
        .then((res) => {
          if (res?.data?.deleteTrainingVideo?.success === false) {
            throw new Error(res.data.deleteTrainingVideo.message || "Delete failed");
          }
          message.success("Video deleted successfully!");
          removeLocally();
        })
        .catch((err) => {
          if (isSchemaMissingError(err)) {
            trainingApiAvailability.deleteTrainingVideo = false;
            removeLocally();
            message.success("Video deleted successfully!");
          } else {
            message.error("Delete failed: " + err.message);
          }
        });
    } else {
      removeLocally();
      message.success("Video deleted successfully!");
    }
    setDeleteCandidate(null);
  };

  return (
    <div className="px-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 font-['Inter'] text-[13px] font-medium text-[#AD0101] transition-colors hover:text-[#8C0101]"
      >
        <ArrowLeft size={14} strokeWidth={2.5} /> Back
      </button>

      {/* Course header card */}
      <div className="mt-3 flex items-start justify-between rounded-[16px] border border-[#CBC8C8] bg-white p-6">
        {courseLoading ? (
          <div className="flex min-h-[100px] w-full items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#AD0101] border-t-transparent" />
          </div>
        ) : !course ? (
          <p className="py-8 text-center font-['Inter'] text-[14px] text-[#A0A0A0]">
            No Data Available
          </p>
        ) : (
          <>
            <div className="flex items-center gap-5">
              <div className="flex h-[115px] w-[146px] shrink-0 items-center justify-center rounded-[12px] border border-[#CBC8C8] bg-white p-2">
                {course.thumbnailPreview && (
                  <img
                    src={course.thumbnailPreview}
                    alt=""
                    className="max-h-full max-w-full object-contain"
                  />
                )}
              </div>
              <div className="flex min-w-0 flex-col gap-1.5">
                <h1 className="font-['Inter'] text-[24px] font-semibold leading-[21px] text-[#101828]">
                  {course.title}
                </h1>
                <p className="max-w-[398px] font-['Inter'] text-[14px] leading-[150%] text-[#9CA3AF]">
                  {course.description}
                </p>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1 font-['Inter'] text-[14px] leading-[18px] text-[#888888]">
                    <img src={TrainingModuleTimingIcon} alt="" className="h-4 w-4 object-contain" />
                    {course.duration}
                  </span>
                  <span className="flex items-center gap-1 font-['Inter'] text-[14px] leading-[18px] text-[#888888]">
                    <Video size={15} className="text-[#888888]" strokeWidth={1.8} />
                    {course.videoCount} Videos
                  </span>
                </div>
              </div>
            </div>
            {course.productStatus === "DRAFT" && course.publishDate ? (
              <span className="inline-flex shrink-0 items-center rounded-[6px] bg-[#054DA2] px-[10px] py-1 text-[12px] font-semibold text-white">
                Yet to publish on {formatPublishDate(course.publishDate)}
              </span>
            ) : course.productStatus === "ACTIVE" ? (
              <span className="inline-flex shrink-0 items-center rounded-[6px] bg-[#1FA55A] px-[10px] py-1 text-[12px] font-semibold text-white">
                Active
              </span>
            ) : null}
          </>
        )}
      </div>

      {/* Journey Videos */}
      <div className="mt-4 rounded-[8px] border border-[#CBC8C8] bg-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-['Inter'] text-[20px] font-semibold leading-[100%] tracking-[-0.2px] text-black">
            Journey Videos
          </h2>
          <button
            type="button"
            onClick={() =>
              navigate("/add-course-video", {
                state: { ...location.state, editing: null },
              })
            }
            className="rounded-[8px] bg-[#AD0101] px-5 py-2.5 font-['Inter'] text-[13px] font-semibold text-white transition-colors hover:bg-[#8C0101]"
          >
            Add Videos
          </button>
        </div>

        {visibleVideos.length > 0 && (
          <div className="mt-3 flex justify-center">
            <span className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] px-[10px] py-1 font-['Inter'] text-[12px] text-[#888888]">
              <GripVertical size={12} /> Drag to reorder
            </span>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-6">
          {courseLoading ? (
            <div className="flex min-h-[100px] items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#AD0101] border-t-transparent" />
            </div>
          ) : visibleVideos.length === 0 ? (
            <p className="py-12 text-center font-['Inter'] text-[14px] text-[#A0A0A0]">
              No Data Available
            </p>
          ) : (
            visibleVideos.map((video, index) => (
              <div
                key={video.id}
                draggable
                onDragStart={() => (dragIndex.current = index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(index)}
                className="flex items-center gap-[30px] rounded-[15px] border border-[#CBC8C8] bg-white px-2 py-3"
              >
                <GripVertical
                  size={18}
                  className="ml-1 shrink-0 cursor-grab text-[#C9C9C9] active:cursor-grabbing"
                />

                {/* Number badge + thumbnail */}
                <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[#AD0101] font-['Inter'] text-[13px] font-semibold text-white">
                  {index + 1}
                </span>
                {video.thumbnail ? (
                  <div className="relative h-[88px] w-[96px] shrink-0">
                    <img
                      src={video.thumbnail}
                      alt=""
                      className="h-[88px] w-[96px] rounded-[10px] object-cover"
                    />
                    <Play
                      size={22}
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white"
                      fill="white"
                    />
                  </div>
                ) : (
                  <div className="flex h-[88px] w-[96px] shrink-0 items-center justify-center rounded-[10px] bg-[#E8B4B4]">
                    <Play size={26} className="text-[#AD0101]" fill="#AD0101" />
                  </div>
                )}

                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <h3 className="font-['Inter'] text-[15px] font-semibold text-[#101828]">
                    {video.title}
                  </h3>
                  <Tooltip title={video.description} placement="topLeft">
                    <p className="max-w-[420px] cursor-default truncate font-['Inter'] text-[13px] text-[#9CA3AF]">
                      {video.description}
                    </p>
                  </Tooltip>
                  <div className="mt-0.5 flex items-center gap-3">
                    <span className="flex items-center gap-1 font-['Inter'] text-[13px] leading-[18px] text-[#888888]">
                      <img
                        src={TrainingModuleTimingIcon}
                        alt=""
                        className="h-3.5 w-3.5 object-contain"
                      />
                      {video.duration}
                    </span>
                    {video.language && (
                      <span className="flex items-center gap-1 font-['Inter'] text-[13px] leading-[18px] text-[#888888]">
                        <img
                          src={TrainingModuleLanguageIcon}
                          alt=""
                          className="h-3.5 w-3.5 object-contain"
                        />
                        {video.language}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-2 pr-1">
                  <button
                    type="button"
                    onClick={() =>
                      navigate("/add-course-video", {
                        state: { ...location.state, editing: video },
                      })
                    }
                    title="Edit"
                  >
                    <img src={TrainingModuleEditIcon} alt="Edit" className="h-8 w-8 object-contain" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteCandidate(video)}
                    title="Delete"
                  >
                    <img
                      src={TrainingModuleDeleteIcon}
                      alt="Delete"
                      className="h-8 w-8 object-contain"
                    />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      <Modal
        open={!!deleteCandidate}
        onCancel={() => setDeleteCandidate(null)}
        footer={null}
        centered
        width={460}
      >
        <div className="p-2">
          <h3 className="mb-2 text-lg font-semibold text-[#1F2020]">
            Are you sure you want to delete the Video?
          </h3>
          <p className="mb-6 text-sm text-[#888888]">
            Are you sure you want to delete "{deleteCandidate?.title}"? This action will
            permanently remove the video from this course.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setDeleteCandidate(null)}
              className="rounded-lg border border-gray-300 px-6 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-lg bg-[#AD0101] px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-[#8C0101]"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CourseModuleDetail;
